import React, { useEffect, useState, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { notification } from "antd";
import LottieLoader from "../../Components/common/LottieLoader";
import {
  PhoneOutlined,
  ArrowRightOutlined,
  AudioOutlined,
  CheckOutlined,
  ExpandOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { ThemeModal } from "../Styles/Theme";
import { useConversation } from "@elevenlabs/react";
import {
  getCampaignByLinkApi,
  getCampaignByIdReset,
} from "../../store/actions/campaignAction";
import {
  getRespondentByTransactionIdApi,
  getRespondentReset,
} from "../../store/actions/surveyRespondentAction";
import {
  LandingPageContainer,
  BackgroundContainer,
  LandingPageWrapper,
  Logo,
  ContentCard,
  Title,
  MessageText,
  NextButton,
  ProfileSection,
  AIIcon,
  ProfileName,
  InstructionsText,
  CityButton,
  CityButtonGroup,
  PinSection,
  PinLabel,
  PinBox,
  PinBoxGroup,
  WebCallSection,
  WebCallTitle,
  WebCallSubtitle,
  WebCallButton,
  CallStatusIndicator,
  OrDivider,
  CallOptionsContainer,
  PhoneCallSection,
  ActiveCallCard,
  CallHeader,
  CallAvatar,
  CallHeaderInfo,
  CallStudyName,
  CallPinBadge,
  CallHeaderRight,
  LiveBadge,
  CallContent,
  TimerDisplay,
  WaveformContainer,
  WaveformBar,
  MediaDisplayArea,
  MediaExpandButton,
  MediaQuestionText,
  ActionButtonsRow,
  ActionButtonWrapper,
  ActionCircle,
  ActionLabel,
  MutedBubble,
  NoiseBubble,
  MediaPreviewModalStyle,
} from "./CampaignLandingPageStyle";

// Parse prompt_questions XML to build mediaMap: { media_id: { url, type, filename, questionText } }
const buildMediaMap = (promptQuestions) => {
  if (!promptQuestions || !promptQuestions.includes('has_media="true"')) return {};
  const map = {};
  const tagRegex = /<(?:question|followup)([^>]*has_media="true"[^>]*)>\s*([\s\S]*?)\s*<(?:break|followup|\/question)/g;
  let match;
  while ((match = tagRegex.exec(promptQuestions)) !== null) {
    const attrs = match[1];
    const questionText = (match[2] || '').trim();
    const get = (name) => {
      const m = attrs.match(new RegExp(`${name}="([^"]*)"`));
      return m ? m[1] : '';
    };
    const mediaId = get('media_id');
    const fileKey = get('media_file_key');
    const mediaType = get('media_type');
    if (mediaId || fileKey) {
      const unescape = (s) => (s || '').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
      let url = unescape(get('media_url'));
      // For YouTube links, recover embed URL from media_id (yt_<videoId>) or media_original_url if url is blank
      if (mediaType === 'link' && !url) {
        const ytIdFromId = mediaId?.startsWith('yt_') ? mediaId.slice(3) : null;
        if (ytIdFromId) {
          url = `https://www.youtube-nocookie.com/embed/${ytIdFromId}?rel=0&modestbranding=1&controls=1`;
        } else {
          const origUrl = unescape(get('media_original_url'));
          const m2 = origUrl?.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
          if (m2) url = `https://www.youtube-nocookie.com/embed/${m2[1]}?rel=0&modestbranding=1&controls=1`;
        }
      }
      map[mediaId || fileKey] = {
        mediaId: mediaId || fileKey,
        url,
        type: mediaType,
        filename: unescape(get('media_filename')),
        questionText,
      };
    }
  }
  return map;
};

// Format seconds to HH:MM:SS
const formatTimer = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Seeded pseudo-random for consistent waveform shape across renders
const _sr = (s) => { const x = Math.sin(s * 9301 + 49297) * 233280; return x - Math.floor(x); };

// Generate waveform bars — tiny dots when idle, animate on voice activity
const WAVE_BARS = Array.from({ length: 80 }, (_, i) => {
  const r = _sr(i + 1);
  return { height: 0.05 + r * 0.04 }; // 5-9% = visible bars when idle
});

// Background-noise pill thresholds. Mic input volume is 0..1 (from the SDK's
// getInputVolume / analyser fallback). While the agent is speaking the
// participant should be silent, so sustained mic input above NOISE_ON_LEVEL
// for NOISE_ON_SUSTAIN_MS signals a noisy room (e.g. a TV). Debounced both
// ways so the pill never flickers. These are starting values — calibrate on a
// real call (raise NOISE_ON_LEVEL if it false-fires, lower it if it misses).
const NOISE_ON_LEVEL = 0.12;        // mic level counted as "noise" while agent speaks
const NOISE_OFF_LEVEL = 0.06;       // mic level counted as "quiet again"
const NOISE_ON_SUSTAIN_MS = 2500;   // must stay noisy this long before showing the pill
const NOISE_OFF_SUSTAIN_MS = 2500;  // must stay quiet this long before hiding the pill

// Pre-compute gradient colors: #2E45DA (left) → #506ED3 (right) matching Figma
const BAR_COLORS = WAVE_BARS.map((_, i) => {
  const t = i / 79;
  const r = Math.round(61 + (120 - 61) * t);
  const g = Math.round(41 + (81 - 41) * t);
  const b = Math.round(105 + (207 - 105) * t);
  return `rgb(${r}, ${g}, ${b})`;
});

// AI assistant avatar icon (matches Figma — purple circle with robot face)
const AIAvatarIcon = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="24" fill="#2E45DA"/>
    <path d="M32 36H16C15.447 36 15 35.553 15 35C15 31.691 17.691 29 21 29H27C30.309 29 33 31.691 33 35C33 35.553 32.553 36 32 36ZM21.987 21.4H23.054L22.535 18.906L21.987 21.4ZM32.5 18H31.949C31.697 15.756 29.81 14 27.5 14H25V13C25 12.448 24.553 12 24 12C23.447 12 23 12.448 23 13V14H20.5C18.19 14 16.303 15.756 16.051 18H15.5C14.673 18 14 18.673 14 19.5V21.5C14 22.327 14.673 23 15.5 23H16.051C16.303 25.244 18.19 27 20.5 27H27.5C29.81 27 31.697 25.244 31.949 23H32.5C33.327 23 34 22.327 34 21.5V19.5C34 18.673 33.327 18 32.5 18ZM24.273 24C23.888 24.017 23.543 23.754 23.465 23.375L23.387 23H21.636L21.552 23.383C21.473 23.743 21.154 24 20.785 24C20.286 24 19.913 23.54 20.017 23.052L21.01 18.384C21.156 17.629 21.77 17.001 22.533 17.001H22.535C23.317 17.005 23.903 17.604 24.069 18.424L25.042 23.054C25.144 23.542 24.772 24 24.274 24H24.273ZM28 23.2C28 23.642 27.642 24 27.2 24C26.758 24 26.4 23.642 26.4 23.2V17.801C26.4 17.359 26.758 17.001 27.2 17.001C27.642 17.001 28 17.359 28 17.801V23.2Z" fill="white"/>
  </svg>
);

// Phone hangup icon (clean, like iPhone end call)
const PhoneOffIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 01-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" fill="currentColor"/>
  </svg>
);

// Filled mic-on icon
const MicOnIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 1a3.5 3.5 0 0 0-3.5 3.5v7a3.5 3.5 0 1 0 7 0v-7A3.5 3.5 0 0 0 12 1zm-1.5 3.5a1.5 1.5 0 1 1 3 0v7a1.5 1.5 0 1 1-3 0v-7z" fill="currentColor"/>
    <path d="M5.5 10a1 1 0 0 1 1 1v.5a5.5 5.5 0 1 0 11 0V11a1 1 0 1 1 2 0v.5a7.502 7.502 0 0 1-6.5 7.435V21h2.5a1 1 0 1 1 0 2h-7a1 1 0 1 1 0-2H11v-2.065A7.502 7.502 0 0 1 4.5 11.5V11a1 1 0 0 1 1-1z" fill="currentColor"/>
  </svg>
);

// Filled mic-off icon (matches Figma fi:mic-off)
const MicOffIcon = () => (
  <svg width="26" height="26" viewBox="0 0 26.25 26.25" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Mic body with cutoff */}
    <path d="M0.675049 8.83626C1.08373 8.66698 1.55426 8.76061 1.86707 9.07338L7.46719 14.6735C7.67227 14.8786 7.78764 15.1568 7.78763 15.4468C7.78754 15.737 7.67252 16.0161 7.46719 16.2212C6.85536 16.8323 6.07551 17.2479 5.22736 17.4164C4.37926 17.5849 3.50016 17.4983 2.70126 17.1676C1.90231 16.8367 1.21885 16.2767 0.738068 15.5579C0.257293 14.8392 0.000761624 13.9937 0 13.129V9.8467C4.06255e-05 9.40439 0.266433 9.00556 0.675049 8.83626ZM2.1875 13.1269L2.19391 13.2882C2.22198 13.6638 2.34664 14.0267 2.55707 14.3413C2.79746 14.7007 3.13922 14.9813 3.53867 15.1467C3.93798 15.3119 4.37732 15.3548 4.80118 15.2706C4.84729 15.2614 4.89266 15.2495 4.9379 15.2375L2.1875 12.4871V13.1269ZM6.5625 10.2184V4.37688C6.56303 3.83442 6.36212 3.31079 5.99854 2.90822C5.63497 2.50574 5.13466 2.25247 4.59503 2.19792C4.0553 2.1434 3.51401 2.29168 3.07724 2.61342C2.64071 2.93506 2.33931 3.40759 2.23129 3.93895C2.11111 4.53083 1.53396 4.91346 0.942078 4.79344C0.350118 4.67324 -0.0325805 4.09619 0.0875854 3.50423C0.303487 2.44097 0.907013 1.49558 1.78055 0.852097C2.65403 0.208774 3.73573 -0.0868521 4.81506 0.0221718C5.89437 0.131294 6.89495 0.637691 7.62207 1.44276C8.3488 2.24759 8.75079 3.29358 8.75 4.37795V10.2184C8.75 10.8225 8.26031 11.3121 7.65625 11.3121C7.05219 11.3121 6.5625 10.8225 6.5625 10.2184Z" fill="currentColor" transform="translate(8.75, 0)"/>
    {/* Curved base */}
    <path d="M0.00023048 1.09375C0.00023048 0.489757 0.490013 0.000110768 1.09398 0C1.69804 0 2.18773 0.489689 2.18773 1.09375V3.28873C2.17816 4.59638 2.55925 5.87787 3.28255 6.96732C4.00588 8.05675 5.03877 8.90499 6.24764 9.40369C7.45658 9.90237 8.78717 10.0284 10.0683 9.76578C11.3493 9.50312 12.523 8.86379 13.4382 7.92969C13.8609 7.49846 14.5534 7.49109 14.9848 7.91367C15.4162 8.33627 15.4232 9.02878 15.0008 9.4603C13.7806 10.7058 12.2154 11.5593 10.5073 11.9095C8.79922 12.2596 7.02527 12.0905 5.41344 11.4256C3.80166 10.7607 2.42477 9.63002 1.46034 8.17749C0.495955 6.725 -0.01242 5.01722 0.00023048 3.27377V1.09375ZM15.3127 1.09375C15.3127 0.489757 15.8025 0.000111038 16.4065 0C17.0105 0 17.5002 0.489689 17.5002 1.09375V3.28232C17.4998 3.79774 17.4534 4.31201 17.3624 4.81934C17.2558 5.41384 16.688 5.80921 16.0935 5.70267C15.499 5.59601 15.1037 5.02823 15.2102 4.43375C15.2784 4.05347 15.3123 3.66759 15.3127 3.28125V1.09375Z" fill="currentColor" transform="translate(4.375, 9.84)"/>
    {/* Bottom line */}
    <path d="M9.84375 0C10.4478 0 10.9375 0.489689 10.9375 1.09375C10.9375 1.69781 10.4478 2.1875 9.84375 2.1875H1.09375C0.489689 2.1875 0 1.69781 0 1.09375C0 0.489689 0.489689 0 1.09375 0H9.84375Z" fill="currentColor" transform="translate(7.656, 24.063)"/>
    {/* Vertical stem */}
    <path d="M0 5.46875V1.09375C0 0.489689 0.489689 0 1.09375 0C1.69781 0 2.1875 0.489689 2.1875 1.09375V5.46875C2.1875 6.07281 1.69781 6.5625 1.09375 6.5625C0.489689 6.5625 0 6.07281 0 5.46875Z" fill="currentColor" transform="translate(12.031, 19.688)"/>
    {/* Diagonal slash */}
    <path d="M0.320352 0.320352C0.747488 -0.106784 1.43985 -0.106784 1.86698 0.320352L25.9295 24.3829C26.3566 24.81 26.3566 25.5023 25.9295 25.9295C25.5023 26.3566 24.81 26.3566 24.3829 25.9295L0.320352 1.86698C-0.106784 1.43985 -0.106784 0.747488 0.320352 0.320352Z" fill="currentColor"/>
  </svg>
);

const CampaignLandingPage = (props) => {
  const { slug, code } = useParams();
  const [searchParams] = useSearchParams();
  const { getCampaignByLink, getCampaignByLinkReset, getCampaignByIdState, getRespondent, getRespondentState, getRespondentReset } = props;
  const [currentStep, setCurrentStep] = useState(1);
  const [pin, setPin] = useState("------");
  const [invalidAccess, setInvalidAccess] = useState(false);
  const [surveyClosed, setSurveyClosed] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [pinExpired, setPinExpired] = useState(false);
  const [isNoisy, setIsNoisy] = useState(false);

  // Active call state
  const [micMuted, setMicMuted] = useState(false);
  const [callSeconds, setCallSeconds] = useState(0);
  const [showingMedia, setShowingMedia] = useState(null);
  const [mediaModalVisible, setMediaModalVisible] = useState(false);
  const [readyTimer, setReadyTimer] = useState(0);
  const [readyEnabled, setReadyEnabled] = useState(false);
  const mediaResolveRef = useRef(null);
  const mediaMapRef = useRef({});
  const readyTimerRef = useRef(null);
  const autoTimeoutRef = useRef(null);
  const videoDurationRef = useRef(0);
  const showingMediaRef = useRef(null);
  const reCallSafetyRef = useRef(null);
  const mediaDismissedRef = useRef(false);
  const timerRef = useRef(null);
  const waveformRef = useRef(null);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const micStreamRef = useRef(null);
  const animFrameRef = useRef(null);
  const isSpeakingRef = useRef(false);
  const micMutedRef = useRef(false);
  const conversationRef = useRef(null);
  const smoothedHeightsRef = useRef(WAVE_BARS.map(b => b.height));
  // Background-noise pill: current shown state + debounce timestamps (refs so
  // the per-frame animation loop reads/writes them without re-rendering).
  const isNoisyRef = useRef(false);
  const noiseAboveSinceRef = useRef(0);
  const noiseQuietSinceRef = useRef(0);

  const conversation = useConversation({
    micMuted,
    clientTools: {
      display_media: async (parameters) => {
        const mediaId = parameters.media_id;
        let media = mediaMapRef.current[mediaId];

        // Retry briefly if mediaMap not yet populated (race with campaign data load)
        if (!media && Object.keys(mediaMapRef.current).length === 0) {
          for (let i = 0; i < 5; i++) {
            await new Promise(r => setTimeout(r, 500));
            media = mediaMapRef.current[mediaId];
            if (media) break;
          }
        }

        if (!media || !media.url) {
          console.warn('[Media] Media not found for id:', mediaId);
          return "Media could not be displayed. Please continue with the question.";
        }

        // User already clicked "I'm Ready" during auto-extend gap — don't re-show
        if (mediaDismissedRef.current) {
          mediaDismissedRef.current = false;
          return "The participant has already finished viewing the media and is ready to respond. Ask for their response now.";
        }

        // Auto-extend re-call: same media already showing — skip speech delay, just reset timer
        if (showingMediaRef.current && showingMediaRef.current.mediaId === media.mediaId) {
          // Clear the re-call safety timer — agent complied
          if (reCallSafetyRef.current) { clearTimeout(reCallSafetyRef.current); reCallSafetyRef.current = null; }
          // Reset the auto-timeout for another cycle
          if (autoTimeoutRef.current) clearTimeout(autoTimeoutRef.current);
          autoTimeoutRef.current = setTimeout(() => {
            if (mediaResolveRef.current) {
              const resolve = mediaResolveRef.current;
              mediaResolveRef.current = null;
              resolve("[MEDIA_STILL_VIEWING]");
              // Silent channel instructs agent to re-call display_media without speaking
              try { conversationRef.current?.sendContextualUpdate("Participant is still reviewing media. Do not speak. Call display_media again immediately with the same media_id."); } catch (e) {}
              // Safety: if agent doesn't re-call within 15s, dismiss media
              reCallSafetyRef.current = setTimeout(() => {
                if (showingMediaRef.current && !mediaResolveRef.current) {
                  setShowingMedia(null);
                  setMediaModalVisible(false);
                  setMicMuted(false);
                  if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
                  setReadyTimer(0);
                  setReadyEnabled(false);
                }
              }, 15000);
            }
          }, 100 * 1000);
          return new Promise((resolve) => {
            mediaResolveRef.current = resolve;
          });
        }

        // First display — resolve any existing pending media Promise (different media switching)
        if (mediaResolveRef.current) {
          mediaResolveRef.current("Switching to new media.");
          mediaResolveRef.current = null;
        }
        mediaDismissedRef.current = false;
        // Wait for agent to finish speaking the question before showing media
        await new Promise(r => setTimeout(r, 4000));

        setShowingMedia(media);
        setMicMuted(true);

        return new Promise((resolve) => {
          mediaResolveRef.current = resolve;
        });
      },
    },
    onConnect: () => {},
    onDisconnect: () => {
      // Clean up any pending media display and auto-extend state
      if (mediaResolveRef.current) {
        mediaResolveRef.current("Call ended");
        mediaResolveRef.current = null;
      }
      if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
      if (autoTimeoutRef.current) { clearTimeout(autoTimeoutRef.current); autoTimeoutRef.current = null; }
      if (reCallSafetyRef.current) { clearTimeout(reCallSafetyRef.current); reCallSafetyRef.current = null; }
      mediaDismissedRef.current = false;
      setShowingMedia(null);
      setMicMuted(false);
    },
    onError: (error) => {
      console.error('[ElevenLabs] Error:', error);
      notification.error({
        message: 'Call Error',
        description: error?.message || 'An error occurred during the call',
      });
    },
    onMessage: () => {},
  });

  // Keep ref for animation loop volume access
  conversationRef.current = conversation;

  // Track speaking/mute state in refs for animation loop
  useEffect(() => {
    isSpeakingRef.current = conversation.isSpeaking;
  }, [conversation.isSpeaking]);

  useEffect(() => {
    micMutedRef.current = micMuted;
  }, [micMuted]);

  // Sync showingMedia state to ref for closure access in display_media handler
  useEffect(() => {
    showingMediaRef.current = showingMedia;
  }, [showingMedia]);

  const agentId = getCampaignByIdState.data?.data?.polling_agent_id?.agent_id ||
                  getCampaignByIdState.data?.polling_agent_id?.agent_id || null;

  // Build mediaMap when campaign data loads
  useEffect(() => {
    const campaignData = getCampaignByIdState.data?.data || getCampaignByIdState.data;
    const pq = campaignData?.polling_agent_id?.prompt_questions;
    if (pq) {
      mediaMapRef.current = buildMediaMap(pq);
    }
  }, [getCampaignByIdState.data]);

  // Call timer
  useEffect(() => {
    if (conversation.status === 'connected') {
      timerRef.current = setInterval(() => {
        setCallSeconds(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (conversation.status === 'disconnected') {
        setCallSeconds(0);
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [conversation.status]);

  // Waveform animation with directional voice reactivity
  // Agent voice animates from left (darker) side, user mic from right (lighter) side
  useEffect(() => {
    if (conversation.status !== 'connected') {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
        micStreamRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
      analyserRef.current = null;
      smoothedHeightsRef.current = WAVE_BARS.map(b => b.height);
      // Clear the noise pill when the call is not connected.
      isNoisyRef.current = false;
      noiseAboveSinceRef.current = 0;
      noiseQuietSinceRef.current = 0;
      setIsNoisy(false);
      return;
    }

    // Start mic analyzer for user voice detection
    let mounted = true;
    const initAnalyzer = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (!mounted) {
          // Component unmounted while awaiting getUserMedia — clean up immediately
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        micStreamRef.current = stream;
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser);
        analyserRef.current = analyser;
      } catch (e) {
        console.error('[Waveform] Mic analyzer init failed:', e);
      }
    };
    initAnalyzer();

    // Animation loop — agent animates from left, user mic animates from right
    const animate = () => {
      const container = waveformRef.current;
      if (!container) {
        animFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      const conv = conversationRef.current;
      const t = performance.now() / 1000;
      const bars = container.children;
      const smoothed = smoothedHeightsRef.current;

      // Get agent output volume from ElevenLabs SDK (real voice reactivity)
      let agentVol = 0;
      if (conv && typeof conv.getOutputVolume === 'function') {
        agentVol = conv.getOutputVolume();
      } else if (isSpeakingRef.current) {
        // Fallback: simulated volume when SDK method not available
        agentVol = Math.max(0, 0.4 + 0.2 * Math.sin(t * 3.1) + 0.15 * Math.sin(t * 7.3));
      }

      // Get user mic volume — prefer SDK, fallback to analyzer
      let userVol = 0;
      if (conv && typeof conv.getInputVolume === 'function') {
        userVol = conv.getInputVolume();
      } else {
        const analyser = analyserRef.current;
        if (analyser) {
          const freqData = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(freqData);
          let sum = 0;
          for (let j = 0; j < freqData.length; j++) sum += freqData[j];
          userVol = sum / (freqData.length * 255);
        }
      }

      // ── Background-noise pill ──────────────────────────────────────
      // While the agent is speaking the participant should be silent, so
      // sustained mic input during that window = ambient room noise (e.g. a
      // TV). Gated on agent-speaking so the participant's own answers are
      // never mistaken for noise. Debounced both directions (show + hide) so
      // the pill flashes in only on real, sustained noise and clears itself
      // once the room is quiet again.
      const nowMs = performance.now();
      if (isSpeakingRef.current && !micMutedRef.current && !showingMediaRef.current) {
        if (userVol > NOISE_ON_LEVEL) {
          noiseQuietSinceRef.current = 0;
          if (!noiseAboveSinceRef.current) noiseAboveSinceRef.current = nowMs;
          if (!isNoisyRef.current && nowMs - noiseAboveSinceRef.current > NOISE_ON_SUSTAIN_MS) {
            isNoisyRef.current = true;
            setIsNoisy(true);
          }
        } else if (userVol < NOISE_OFF_LEVEL) {
          noiseAboveSinceRef.current = 0;
          if (isNoisyRef.current) {
            if (!noiseQuietSinceRef.current) noiseQuietSinceRef.current = nowMs;
            if (nowMs - noiseQuietSinceRef.current > NOISE_OFF_SUSTAIN_MS) {
              isNoisyRef.current = false;
              setIsNoisy(false);
              noiseQuietSinceRef.current = 0;
            }
          }
        }
      }

      // Time seed for per-bar variation (changes ~8x/sec for organic wiggle)
      const tSlice = Math.floor(t * 8);

      for (let i = 0; i < WAVE_BARS.length && i < bars.length; i++) {
        const pos = i / 79; // 0 = left (AI/dark), 1 = right (user/light)
        const base = WAVE_BARS[i].height;

        // Agent influence — peaks on left, leaks slightly past center
        let agentH = 0;
        if (agentVol > 0.005) {
          const falloff = Math.pow(Math.max(0, 1 - pos * 1.5), 2);
          // Each bar wiggles independently (seeded random changes with time)
          const barVar = 0.3 + _sr(i * 7 + tSlice * 3) * 0.7;
          agentH = falloff * agentVol * barVar * 5.0;
        }

        // User mic — peaks on right, only when unmuted and above noise floor
        let userH = 0;
        if (userVol > 0.005 && !micMutedRef.current) {
          const falloff = Math.pow(Math.max(0, 1 - (1 - pos) * 1.5), 2);
          const barVar = 0.3 + _sr(i * 11 + tSlice * 5) * 0.7;
          userH = falloff * userVol * barVar * 5.0;
        }

        // Small bars idle, wiggle up proportional to voice volume
        const target = base + Math.max(agentH, userH);

        // Smooth interpolation
        smoothed[i] += (target - smoothed[i]) * 0.25;
        const finalH = Math.max(0.05, Math.min(0.95, smoothed[i]));

        bars[i].style.height = `${Math.round(finalH * 100)}%`;
      }

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      mounted = false;
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
        micStreamRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
      analyserRef.current = null;
    };
  }, [conversation.status]);

  const handleStartCall = useCallback(async () => {
    if (!agentId) {
      notification.error({
        message: 'Cannot Start Call',
        description: 'No AI agent is configured for this study',
      });
      return;
    }

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await conversation.startSession({ agentId });
    } catch (error) {
      console.error('[ElevenLabs] Failed to start call:', error);
      if (error.name === 'NotAllowedError') {
        notification.error({
          message: 'Microphone Access Required',
          description: 'Please allow microphone access to start the call',
        });
      } else {
        notification.error({
          message: 'Failed to Start Call',
          description: error?.message || 'Could not connect to the AI agent',
        });
      }
    }
  }, [agentId, conversation]);

  const handleEndCall = useCallback(async () => {
    try {
      await conversation.endSession();
    } catch (error) {
      console.error('[ElevenLabs] Failed to end call:', error);
    }
  }, [conversation]);

  const handleToggleMute = useCallback(() => {
    if (showingMedia) return; // Disabled during media display
    setMicMuted(prev => !prev);
  }, [showingMedia]);

  // Countdown timer for "I'm Ready" button based on media type
  const startReadyCountdown = useCallback((mediaType) => {
    if (readyTimerRef.current) clearInterval(readyTimerRef.current);
    setReadyEnabled(false);
    // Base delay: image=5s, pdf/link=15s, video=handled by onLoadedMetadata
    let delay = mediaType === 'image' ? 5 : (mediaType === 'pdf' || mediaType === 'link') ? 15 : 10;
    if (mediaType === 'video' && videoDurationRef.current > 0) {
      delay = Math.min(Math.ceil(videoDurationRef.current), 30);
    }
    setReadyTimer(delay);
    readyTimerRef.current = setInterval(() => {
      setReadyTimer(prev => {
        if (prev <= 1) {
          clearInterval(readyTimerRef.current);
          readyTimerRef.current = null;
          setReadyEnabled(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Auto-timeout: resolves at 100s (20s buffer before ElevenLabs 120s hard limit)
  // Instead of dismissing media, triggers auto-extend loop — tells agent to re-call display_media
  const startAutoTimeout = useCallback(() => {
    if (autoTimeoutRef.current) clearTimeout(autoTimeoutRef.current);
    const timeout = 100; // 100s — 20s buffer before ElevenLabs 120s limit
    autoTimeoutRef.current = setTimeout(() => {
      if (mediaResolveRef.current) {
        // Auto-extend: resolve with re-call instruction, keep media on screen
        const resolve = mediaResolveRef.current;
        mediaResolveRef.current = null;
        resolve("[MEDIA_STILL_VIEWING]");
        // Silent channel instructs agent to re-call display_media without speaking
        try { conversationRef.current?.sendContextualUpdate("Participant is still reviewing media. Do not speak. Call display_media again immediately with the same media_id."); } catch (e) {}
        // Safety: if agent doesn't re-call within 15s, dismiss media
        if (reCallSafetyRef.current) clearTimeout(reCallSafetyRef.current);
        reCallSafetyRef.current = setTimeout(() => {
          if (showingMediaRef.current && !mediaResolveRef.current) {
            setShowingMedia(null);
            setMediaModalVisible(false);
            setMicMuted(false);
            if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
            setReadyTimer(0);
            setReadyEnabled(false);
          }
        }, 15000);
      }
    }, timeout * 1000);
  }, []);

  // Start countdown when media is shown
  useEffect(() => {
    let videoFallbackTimer;
    if (showingMedia) {
      videoDurationRef.current = 0;
      if (showingMedia.type !== 'video') {
        startReadyCountdown(showingMedia.type);
        startAutoTimeout();
      } else {
        // Fallback: if video onLoadedMetadata doesn't fire within 5s, use default 10s countdown
        videoFallbackTimer = setTimeout(() => {
          if (videoDurationRef.current === 0) {
            startReadyCountdown('video');
            startAutoTimeout();
          }
        }, 5000);
      }
    } else {
      if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
      if (autoTimeoutRef.current) { clearTimeout(autoTimeoutRef.current); autoTimeoutRef.current = null; }
      if (reCallSafetyRef.current) { clearTimeout(reCallSafetyRef.current); reCallSafetyRef.current = null; }
      setReadyTimer(0);
      setReadyEnabled(false);
    }
    return () => {
      if (videoFallbackTimer) clearTimeout(videoFallbackTimer);
      if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
      if (autoTimeoutRef.current) { clearTimeout(autoTimeoutRef.current); autoTimeoutRef.current = null; }
      if (reCallSafetyRef.current) { clearTimeout(reCallSafetyRef.current); reCallSafetyRef.current = null; }
    };
  }, [showingMedia, startReadyCountdown, startAutoTimeout]);

  // Safety: if agent starts speaking while media is stuck (no pending Promise = tool timed out),
  // auto-dismiss the media so the participant can respond
  useEffect(() => {
    if (conversation.isSpeaking && showingMedia && !mediaResolveRef.current) {
      // Agent moved on but media is stuck — clean up
      setShowingMedia(null);
      setMediaModalVisible(false);
      setMicMuted(false);
      if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
      if (autoTimeoutRef.current) { clearTimeout(autoTimeoutRef.current); autoTimeoutRef.current = null; }
      if (reCallSafetyRef.current) { clearTimeout(reCallSafetyRef.current); reCallSafetyRef.current = null; }
      setReadyTimer(0);
      setReadyEnabled(false);
    }
  }, [conversation.isSpeaking, showingMedia]);

  const handleReady = useCallback(() => {
    setShowingMedia(null);
    setMediaModalVisible(false);
    setMicMuted(false);
    if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
    if (autoTimeoutRef.current) { clearTimeout(autoTimeoutRef.current); autoTimeoutRef.current = null; }
    if (reCallSafetyRef.current) { clearTimeout(reCallSafetyRef.current); reCallSafetyRef.current = null; }
    setReadyTimer(0);
    setReadyEnabled(false);
    // Flag so that if agent re-calls display_media during the gap, we don't re-show
    mediaDismissedRef.current = true;
    if (mediaResolveRef.current) {
      mediaResolveRef.current("User has reviewed the media and is ready. Now ask for their response to the question.");
      mediaResolveRef.current = null;
    }
  }, []);

  const getCallStatusText = () => {
    switch (conversation.status) {
      case 'connecting': return 'Connecting...';
      case 'connected': return 'Call in progress';
      case 'disconnecting': return 'Ending call...';
      default: return 'Ready to call';
    }
  };

  // Fetch campaign data
  useEffect(() => {
    if (slug && code) {
      getCampaignByLink(slug, code);
    }
  }, [slug, code, getCampaignByLink]);

  // Handle campaign data response
  useEffect(() => {
    if (getCampaignByIdState.apiState === 'success') {
      const campaignData = getCampaignByIdState.data?.data || getCampaignByIdState.data;
      if (campaignData?.status === 'completed' || campaignData?.status === 'closed') {
        setSurveyClosed(true);
      }
    } else if (getCampaignByIdState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getCampaignByIdState.message || 'Study not found',
      });
      getCampaignByLinkReset();
    }
  }, [getCampaignByIdState, getCampaignByLinkReset]);

  // Get existing respondent PIN by transaction_id (sid = per-attempt send id from the invite
  // link, forwarded so the backend can attribute the landing to the exact email attempt)
  useEffect(() => {
    const transaction_id = searchParams.get('transaction_id');
    const sid = searchParams.get('sid');
    if (transaction_id) {
      getRespondent(transaction_id, sid).catch((error) => {
        console.error('Error fetching respondent:', error);
      });
    }
  }, [searchParams, getRespondent]);

  // Handle respondent data response
  useEffect(() => {
    if (getRespondentState.apiState === 'success' && getRespondentState.data) {
      const responseData = getRespondentState.data;
      const respondentData = responseData.data || responseData;
      const pinValue = respondentData.pin;

      if (pinValue) {
        setPin(pinValue);
        setInvalidAccess(false);
      }

      if (respondentData.call_status === 'completed') {
        setAlreadyCompleted(true);
      } else if (
        respondentData.invite_source === 'purespectrum' &&
        respondentData.survey_status === 'abandoned' &&
        !respondentData.call_started_at
      ) {
        setPinExpired(true);
      }

      getRespondentReset();
    } else if (getRespondentState.apiState === 'error') {
      console.error('Failed to fetch respondent:', getRespondentState.message);
      const transaction_id = searchParams.get('transaction_id');
      if (transaction_id) {
        setInvalidAccess(true);
      }
      setPin('------');
      getRespondentReset();
    }
  }, [getRespondentState, getRespondentReset, searchParams]);

  // Parse campaign data
  const responseData = getCampaignByIdState.data;
  const campaign = responseData?.data || responseData || null;

  // A study with no Study Message has nothing to show on the intro step, so the participant is
  // taken straight to the PIN / Start Call screen - one less tap between the invite and the
  // interview. Studies that DO have a message keep the intro step exactly as before.
  // Same emptiness test the study forms use: the message is rich text, so "<p></p>" counts as empty.
  const hasIntroMessage = !!(campaign?.survey_message || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, '')
    .trim();
  // Derived during render, NOT set from an effect: an effect runs after paint, so the participant
  // would see one frame of the empty intro card before it jumped to the PIN screen.
  const effectiveStep = hasIntroMessage ? currentStep : 2;

  const handleNext = () => {
    setCurrentStep(2);
  };

  const phoneNumbers = campaign?.polling_agent_id?.phone_numbers || [];
  const studyName = campaign?.topic || campaign?.campaign_name || "Survey";

  const hasTransactionId = searchParams.get('transaction_id');
  const isRespondentLoading = hasTransactionId && getRespondentState.apiState === 'loading';

  const isCallActive = conversation.status === 'connected';

  // ─── Loading / Error / Status screens ────────────────────────────

  if (getCampaignByIdState.apiState === 'loading' || isRespondentLoading) {
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              {/* inline: without it LottieLoader renders as a fixed full-screen overlay that covers
                  the "Processing... / Loading survey..." text sitting right below it in this card. */}
              <LottieLoader inline size="medium" />
              <Title style={{ marginTop: '24px', marginBottom: '8px' }}>Processing...</Title>
              <MessageText style={{ marginBottom: 0 }}>Loading survey...</MessageText>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  if (!campaign) {
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Title style={{ marginBottom: '16px' }}>Study Not Found</Title>
              <MessageText style={{ color: '#666', lineHeight: '1.6', marginBottom: 0 }}>
                The study you're looking for doesn't exist or has been removed.
              </MessageText>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  if (surveyClosed) {
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Title style={{ marginBottom: '16px' }}>Survey Closed</Title>
              <MessageText style={{ color: '#666', lineHeight: '1.6' }}>
                Uh oh! You missed it. This survey has been closed and is no longer accepting responses.
                <br /><br />
                Thank you for your interest in participating.
              </MessageText>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  if (alreadyCompleted) {
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10003;</div>
              <Title style={{ marginBottom: '16px' }}>Already Completed</Title>
              <MessageText style={{ color: '#666', lineHeight: '1.6', marginBottom: 0 }}>
                You have already completed this survey. Thank you for your participation!
              </MessageText>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  if (pinExpired) {
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#128337;</div>
              <Title style={{ marginBottom: '16px' }}>PIN Expired</Title>
              <MessageText style={{ color: '#666', lineHeight: '1.6', marginBottom: 0 }}>
                Uh oh! Your PIN has expired. Survey PINs are valid for 1 hour after being issued.
                <br /><br />
                Please contact support if you believe this is an error.
              </MessageText>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  if (invalidAccess && campaign?.study_type !== 'email-invite') {
    return (
      <LandingPageContainer>
        <BackgroundContainer />
        <LandingPageWrapper>
          <Logo><img src="/images/logo.svg" alt="CrowdVox" /></Logo>
          <ContentCard>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Title style={{ marginBottom: '16px' }}>Invalid Link</Title>
              <MessageText style={{ color: '#666', lineHeight: '1.6' }}>
                Uh oh! Looks like this survey link is no longer valid.
                <br /><br />
                The link may have expired or been removed. Please contact support if you believe this is an error.
              </MessageText>
            </div>
          </ContentCard>
        </LandingPageWrapper>
      </LandingPageContainer>
    );
  }

  // ─── Main render ─────────────────────────────────────────────────

  return (
    <LandingPageContainer>
      <MediaPreviewModalStyle />
      <BackgroundContainer />
      <LandingPageWrapper>
        <Logo>
          <img src="/images/logo.svg" alt="CrowdVox" />
        </Logo>

        {isCallActive ? (
          // ── Active Call UI ──
          <ActiveCallCard>
            <CallHeader>
              <CallAvatar>
                <AIAvatarIcon size={48} />
              </CallAvatar>
              <CallHeaderInfo>
                <CallStudyName>{studyName}</CallStudyName>
              </CallHeaderInfo>
              <CallPinBadge>
                <span>PIN:</span>
                <span className="pin-digits">{pin}</span>
              </CallPinBadge>
              <CallHeaderRight>
                <LiveBadge>
                  <span className="live-dot" />
                  <span className="live-text">Live Call</span>
                </LiveBadge>
              </CallHeaderRight>
            </CallHeader>

            <CallContent>
              {!showingMedia && <TimerDisplay>{formatTimer(callSeconds)}</TimerDisplay>}
              {showingMedia ? (
                <>
                  <MediaDisplayArea $wide={showingMedia.type === 'pdf'}>
                    {showingMedia.type === 'image' && (
                      <img src={showingMedia.url} alt={showingMedia.filename || 'Media'} />
                    )}
                    {showingMedia.type === 'video' && (
                      <video
                        src={showingMedia.url}
                        muted
                        autoPlay
                        loop
                        controls
                        controlsList="nodownload"
                        onLoadedMetadata={(e) => {
                          const dur = Math.ceil(e.target.duration || 10);
                          videoDurationRef.current = dur;
                          startReadyCountdown('video');
                          startAutoTimeout();
                        }}
                      />
                    )}
                    {showingMedia.type === 'pdf' && (
                      <iframe
                        src={`${showingMedia.url}#toolbar=0&navpanes=0&view=FitH`}
                        title={showingMedia.filename || 'Document'}
                      />
                    )}
                    {showingMedia.type === 'link' && (
                      <iframe
                        src={`${showingMedia.url}`}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="YouTube Video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    )}
                    <MediaExpandButton onClick={() => setMediaModalVisible(true)}>
                      <ExpandOutlined />
                    </MediaExpandButton>
                  </MediaDisplayArea>
                  <MediaQuestionText>
                    <div className="helper-text">
                      <InfoCircleOutlined /> Your mic is muted. Press "I'm ready to answer" when done viewing.
                    </div>
                  </MediaQuestionText>
                </>
              ) : (
                <WaveformContainer ref={waveformRef}>
                  {WAVE_BARS.map((bar, i) => (
                    <WaveformBar
                      key={i}
                      $height={bar.height}
                      style={{ background: BAR_COLORS[i] }}
                    />
                  ))}
                </WaveformContainer>
              )}
            </CallContent>

            <ActionButtonsRow>
              {isNoisy && !micMuted && (
                <NoiseBubble>
                  <span className="noise-dot" />
                  It seems there's too much disturbance - could you consider moving to a quieter place?
                </NoiseBubble>
              )}
              <ActionButtonWrapper>
                <ActionCircle onClick={handleEndCall} style={{ background: '#D94F4F', borderColor: '#D94F4F', color: '#fff' }}>
                  <PhoneOffIcon />
                </ActionCircle>
                <ActionLabel>Hangup</ActionLabel>
              </ActionButtonWrapper>

              {showingMedia && (
                <ActionButtonWrapper>
                  <ActionCircle
                    onClick={readyEnabled ? handleReady : undefined}
                    style={{
                      background: readyEnabled ? '#2E45DA' : 'rgba(46, 69, 218, 0.3)',
                      borderColor: readyEnabled ? '#2E45DA' : 'rgba(46, 69, 218, 0.3)',
                      color: '#fff',
                      cursor: readyEnabled ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {readyTimer > 0 && readyTimer <= 3 ? readyTimer : <CheckOutlined />}
                  </ActionCircle>
                  <ActionLabel>{readyTimer > 3 ? `Wait ${readyTimer}s` : readyTimer > 0 ? 'Almost...' : "I'm ready to answer"}</ActionLabel>
                </ActionButtonWrapper>
              )}

              <ActionButtonWrapper style={{ position: 'relative' }}>
                {micMuted && (
                  <MutedBubble>You are muted</MutedBubble>
                )}
                <ActionCircle
                  className={micMuted ? 'muted' : ''}
                  onClick={handleToggleMute}
                  disabled={!!showingMedia}
                  style={showingMedia ? { opacity: 0.3, cursor: 'not-allowed', background: '#f0f0f0', borderColor: '#ddd', color: '#bbb' } : {}}
                >
                  {micMuted ? <MicOffIcon /> : <MicOnIcon />}
                </ActionCircle>
                <ActionLabel style={showingMedia ? { opacity: 0.3 } : {}}>{micMuted ? 'Unmute' : 'Mute'}</ActionLabel>
              </ActionButtonWrapper>
            </ActionButtonsRow>

            {/* Full-screen media preview modal */}
            <ThemeModal
              title={null}
              open={mediaModalVisible}
              onCancel={() => setMediaModalVisible(false)}
              footer={null}
              width={showingMedia?.type === 'pdf' ? 680 : '95%'}
              centered
              destroyOnClose
              closable
              className="media-preview-modal"
              style={{ maxWidth: showingMedia?.type === 'pdf' ? 680 : 1100 }}
            >
              {showingMedia?.type === 'image' && (
                <img src={showingMedia.url} alt={showingMedia.filename || 'Media'} style={{ width: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block' }} />
              )}
              {showingMedia?.type === 'video' && (
                <video src={showingMedia.url} controls autoPlay controlsList="nodownload" style={{ width: '100%', maxHeight: '85vh', display: 'block' }} />
              )}
              {showingMedia?.type === 'link' && (
                <iframe src={showingMedia.url} style={{ width: '100%', height: '70vh', border: 'none' }} title="YouTube Video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              )}
              {showingMedia?.type === 'pdf' && (
                <iframe src={`${showingMedia.url}#toolbar=0&navpanes=0&view=FitW`} style={{ width: '100%', height: '70vh', border: 'none', background: '#fff' }} title={showingMedia.filename || 'Document'} />
              )}
            </ThemeModal>
          </ActiveCallCard>
        ) : effectiveStep === 1 ? (
          // ── Step 1: Survey Introduction ──
          <ContentCard>
            <Title>{studyName}</Title>
            <MessageText
              dangerouslySetInnerHTML={{
                // No fallback text: an empty Study Message means this step is skipped entirely
                // (see hasIntroMessage above), so there is nothing to substitute here.
                __html: campaign.survey_message || '',
              }}
            />
            <NextButton onClick={handleNext}>
              Next
              <ArrowRightOutlined />
            </NextButton>
          </ContentCard>
        ) : (
          // ── Step 2: Call Instructions ──
          <ContentCard>
            <ProfileSection>
              <AIIcon>
                <AIAvatarIcon size={80} />
              </AIIcon>
              <ProfileName>{studyName}</ProfileName>
            </ProfileSection>

            <PinSection>
              <PinLabel>Your PIN</PinLabel>
              <PinBoxGroup>
                {pin.split('').map((digit, index) => (
                  <PinBox key={index}>{digit}</PinBox>
                ))}
              </PinBoxGroup>
            </PinSection>

            {phoneNumbers.length > 0 && agentId && (
              <InstructionsText>
                Choose how you'd like to start the interview:
              </InstructionsText>
            )}

            <CallOptionsContainer>
              {phoneNumbers.length > 0 && (
                <PhoneCallSection style={!agentId ? { gridColumn: '1 / -1' } : undefined}>
                  <WebCallTitle style={{ marginBottom: '16px' }}>Call by Phone</WebCallTitle>
                  <CityButtonGroup>
                    {phoneNumbers.map((phone, index) => (
                      <CityButton
                        key={index}
                        type="default"
                        icon={<PhoneOutlined />}
                        onClick={() => window.open(`tel:${phone.number.replace(/-/g, '')}`)}
                      >
                        {phone.label}: {phone.number}
                      </CityButton>
                    ))}
                  </CityButtonGroup>
                </PhoneCallSection>
              )}

              {phoneNumbers.length > 0 && agentId && (
                <OrDivider>
                  <span>Or</span>
                </OrDivider>
              )}

              {agentId && (
                <WebCallSection style={phoneNumbers.length === 0 ? { gridColumn: '1 / -1' } : undefined}>
                  <WebCallTitle>Call from Web</WebCallTitle>
                  <WebCallSubtitle>
                    Start directly from your browser
                  </WebCallSubtitle>

                  {(conversation.status === 'connected' || conversation.status === 'connecting') && (
                    <CallStatusIndicator $status={conversation.status}>
                      <span className="status-dot" />
                      {getCallStatusText()}
                    </CallStatusIndicator>
                  )}

                  <WebCallButton
                    className="start-call"
                    onClick={handleStartCall}
                    disabled={conversation.status === 'connecting'}
                    loading={conversation.status === 'connecting'}
                    icon={conversation.status !== 'connecting' && <AudioOutlined />}
                  >
                    {conversation.status === 'connecting' ? 'Connecting...' : 'Start Call'}
                  </WebCallButton>
                </WebCallSection>
              )}
            </CallOptionsContainer>
          </ContentCard>
        )}
      </LandingPageWrapper>
    </LandingPageContainer>
  );
};

const mapStateToProps = (state) => ({
  getCampaignByIdState: state.campaign.getCampaignByIdState,
  getRespondentState: state.surveyRespondent.getRespondentState,
});

const mapDispatchToProps = (dispatch) => ({
  getCampaignByLink: (slug, code) => dispatch(getCampaignByLinkApi(slug, code)),
  getCampaignByLinkReset: () => dispatch(getCampaignByIdReset()),
  getRespondent: (transaction_id, sid) => dispatch(getRespondentByTransactionIdApi(transaction_id, sid)),
  getRespondentReset: () => dispatch(getRespondentReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignLandingPage);
